import { QueryClient, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useSitesInfo } from '@newfold/huapi-js';

import { useMFEContext } from '~/components/MFEProvider';
import useAccount from '~/hooks/useAccount';

import useAMRevamp from './useAMRevamp';

type ProductCodeTypes =
  | 'HOSTING_UPGRADE'
  | 'SITELOCK_ESSENTIALS'
  | 'SITELOCK_FAMILY'
  | 'SITELOCK_UPGRADE'
  | 'YOAST_SEO_PREMIUM'
  | 'SSL_DV'
  | 'SSL_EV_FAMILY'
  | 'BH_ECOMM_ADDON_FAMILY'
  | 'BH_ECOMM_ADDON_PLUS'
  | 'JETPACK_BACKUP_FAMILY'
  | 'JETPACK_BACKUP_T1'
  | 'WP_SOLUTION_FAMILY'
  | 'WP_SOLUTION_UPGRADE'
  | 'DOM_FAMILY'
  | 'BH_PREM_BKUP_ADDL_STORAGE'
  | 'BH_BACKUP_FAMILY'
  | 'XCITIUM_UPGRADE'
  | 'CPANEL_FAMILY'
  | 'CPANEL_UPGRADE'
  | 'BH_BACKUP_UPGRADE'
  | 'PRO_DESIGN_LIVE_FAMILY'
  | 'AI_CREDIT'
  | 'MONARX_FAMILY';

type ProductCodeTypesObject = {
  HOSTING_UPGRADE: 'HOSTING_UPGRADE';
  SITELOCK_ESSENTIALS: 'SITELOCK_ESSENTIALS';
  SITELOCK_FAMILY: 'SITELOCK_FAMILY';
  SITELOCK_UPGRADE: 'SITELOCK_UPGRADE';
  YOAST_SEO_PREMIUM: 'YOAST_SEO_PREMIUM';
  SSL_DV: 'SSL_DV';
  SSL_EV_FAMILY: 'SSL_EV_FAMILY';
  BH_ECOMM_ADDON_FAMILY: 'BH_ECOMM_ADDON_FAMILY';
  CODEGUARD_UPGRADE: 'CODEGUARD_UPGRADE';
  BH_ECOMM_ADDON_PLUS: 'BH_ECOMM_ADDON_PLUS';
  BH_BACKUP_FAMILY: 'BH_BACKUP_FAMILY';
  JETPACK_BACKUP_FAMILY: 'JETPACK_BACKUP_FAMILY';
  JETPACK_BACKUP_T1: 'JETPACK_BACKUP_T1';
  WP_SOLUTION_FAMILY: 'WP_SOLUTION_FAMILY';
  WP_SOLUTION_UPGRADE: 'WP_SOLUTION_UPGRADE';
  DOM_FAMILY: 'DOM_FAMILY';
  BH_PREM_BKUP_ADDL_STORAGE: 'BH_PREM_BKUP_ADDL_STORAGE';
  XCITIUM_UPGRADE: 'XCITIUM_UPGRADE';
  CPANEL_FAMILY: 'CPANEL_FAMILY';
  CPANEL_UPGRADE: 'CPANEL_UPGRADE';
  BH_BACKUP_UPGRADE: 'BH_BACKUP_UPGRADE';
  PRO_DESIGN_LIVE_FAMILY: 'PRO_DESIGN_LIVE_FAMILY';
  AI_CREDIT: 'AI_CREDIT';
  MONARX_FAMILY: 'MONARX_FAMILY';
};

export const productTypes = {
  HOSTING_UPGRADE: 'HOSTING_UPGRADE',
  SITELOCK_ESSENTIALS: 'SITELOCK_ESSENTIALS',
  SITELOCK_FAMILY: 'SITELOCK_FAMILY',
  SITELOCK_UPGRADE: 'SITELOCK_UPGRADE',
  YOAST_SEO_PREMIUM: 'YOAST_SEO_PREMIUM',
  SSL_DV: 'SSL_DV',
  SSL_EV_FAMILY: 'SSL_EV_FAMILY',
  BH_ECOMM_ADDON_FAMILY: 'BH_ECOMM_ADDON_FAMILY',
  CODEGUARD_UPGRADE: 'CODEGUARD_UPGRADE',
  BH_ECOMM_ADDON_PLUS: 'BH_ECOMM_ADDON_PLUS',
  BH_BACKUP_FAMILY: 'BH_BACKUP_FAMILY',
  JETPACK_BACKUP_FAMILY: 'JETPACK_BACKUP_FAMILY',
  JETPACK_BACKUP_T1: 'JETPACK_BACKUP_T1',
  WP_SOLUTION_FAMILY: 'WP_SOLUTION_FAMILY',
  WP_SOLUTION_UPGRADE: 'WP_SOLUTION_UPGRADE',
  DOM_FAMILY: 'DOM_FAMILY',
  BH_PREM_BKUP_ADDL_STORAGE: 'BH_PREM_BKUP_ADDL_STORAGE',
  XCITIUM_UPGRADE: 'XCITIUM_UPGRADE',
  CPANEL_FAMILY: 'CPANEL_FAMILY',
  CPANEL_UPGRADE: 'CPANEL_UPGRADE',
  BH_BACKUP_UPGRADE: 'BH_BACKUP_UPGRADE',
  PRO_DESIGN_LIVE_FAMILY: 'PRO_DESIGN_LIVE_FAMILY',
  AI_CREDIT: 'AI_CREDIT',
  MONARX_FAMILY: 'MONARX_FAMILY',
} as ProductCodeTypesObject;

const getQueryKey = (
  queryType: string,
  productCode: ProductCodeTypes,
  accountId?: string | number | null,
  productData?: {
    siteId?: string;
    siteUrl?: string;
  },
) => {
  return [`/ctb/${queryType}/${accountId}`, { productCode, ...productData }];
};

export const useCtbProductInfo = (
  productCode: ProductCodeTypes,
  prodInstId?: string,
  productName?: string,
  usedBytes?: number,
  allowedLimitBytes?: number,
) => {
  const { t } = useTranslation('clickToBuy', { useSuspense: false });
  const { id } = useAccount();
  const accountId = prodInstId || id;
  const { isAMRevamp } = useAMRevamp();
  const {
    // @ts-expect-error
    data: { hostingData },
    // @ts-expect-error
    brandFromJWT,
  } = useMFEContext();
  const { siteId } = useParams();

  const { data: siteInfo, isPending } = useSitesInfo(Number(siteId), {
    query: {
      enabled: false,
    },
  });

  const siteUrl = siteInfo?.data?.url;

  const hostingPlanData = isAMRevamp
    ? {
        productInstanceId: prodInstId,
        productName,
      }
    : hostingData;

  const parentProdInstId =
    !isPending && !!siteInfo
      ? siteInfo?.data?.account_back_ref
      : hostingPlanData?.productInstanceId;

  const hostingProductName = hostingPlanData?.productName;
  const adTrackingBannerId = isAMRevamp
    ? 'P99C46097236S1N0B1A151D0E0000V105'
    : 'P99C46098237S1N0B1A20D0E0000V102';
  const isBluehost = brandFromJWT === 'bluehost';
  const isHostgator = brandFromJWT === 'hostgator';
  const xcitiumAdTrackingBannerId = isBluehost
    ? 'P180C46097236S1N0B1A151D0E0000V101'
    : isHostgator
    ? 'P180C46098237S1N0B1A20D0E0000V100'
    : '';

  const ctbProducts = {
    [productTypes.HOSTING_UPGRADE]: {
      data: {
        action: 'upgrade',
        prodInstId: parentProdInstId,
        adTrackingBannerId: 'P61C100S1N0B3003A151D115E0000V131',
      },
      name: hostingProductName,
      purchaseQueryKey: getQueryKey('hasPurchased', productCode, accountId),
    },
    [productTypes.BH_ECOMM_ADDON_FAMILY]: {
      data: {
        productCode: productTypes.BH_ECOMM_ADDON_FAMILY,
        parentProdInstId,
        adTrackingBannerId: 'P103C100S1N0B3003A151D115E0000V100',
      },
      name: t('productNames.ecommerce'),
      purchaseQueryKey: getQueryKey('hasPurchased', productCode, accountId),
    },
    [productTypes.BH_ECOMM_ADDON_PLUS]: {
      data: {
        action: 'upgrade',
        prodInstId: parentProdInstId,
        adTrackingBannerId: 'P103C100S1N0B3003A151D115E0000V101',
      },
      name: t('productNames.ecommerce'),
      purchaseQueryKey: getQueryKey('hasPurchased', productCode, accountId),
    },
    [productTypes.SITELOCK_ESSENTIALS]: {
      data: {
        productCode: productTypes.SITELOCK_ESSENTIALS,
        parentProdInstId,
        purchaseTerm: '1',
        purchaseTermUnit: 'YEAR',
        adTrackingBannerId: 'P13C100S1N0B3003A1D115E0000V108',
      },
      name: t('productNames.sitelock'),
      purchaseQueryKey: getQueryKey('hasPurchased', productCode, accountId),
    },
    [productTypes.SITELOCK_FAMILY]: {
      data: {
        productCode: productTypes.SITELOCK_FAMILY,
        parentProdInstId,
        adTrackingBannerId: 'P13C100S1N0B3003A1D115E0000V108',
      },
      name: t('productNames.sitelock'),
      purchaseQueryKey: getQueryKey('hasPurchased', productCode, accountId),
    },
    [productTypes.SITELOCK_UPGRADE]: {
      data: {
        action: 'upgrade',
        purchaseTerm: '1',
        purchaseTermUnit: 'YEAR',
        adTrackingBannerId: 'P13C100S1N0B3003A1D115E0000V108',
      },
      name: t('productNames.sitelock'),
      purchaseQueryKey: getQueryKey('hasPurchased', productCode, accountId),
    },
    [productTypes.YOAST_SEO_PREMIUM]: {
      data: {
        productCode: productTypes.YOAST_SEO_PREMIUM,
        parentProdInstId,
        adTrackingBannerId: 'P167C100S1N0B3003A151D115E0000V100',
        // { parentProduct: { siteId } } // also required, but should be passed in from current UI with data prop
      },
      name: t('productNames.yoast'),
      purchaseQueryKey: getQueryKey('hasPurchased', productCode, accountId, {
        siteId,
      }),
    },
    [productTypes.SSL_DV]: {
      data: {
        productCode: productTypes.SSL_DV,
        adTrackingBannerId: 'P13C100S1N0B3003A151D115E0000V104',
      },
      name: t('productNames.ssl'),
      purchaseQueryKey: getQueryKey('hasPurchased', productCode, accountId, {
        siteId,
      }),
    },
    [productTypes.SSL_EV_FAMILY]: {
      data: {
        productCode: productTypes.SSL_EV_FAMILY,
        adTrackingBannerId: 'P90C100S1N0B3003A20D115E0000V100',
      },
      name: t('productNames.ssl'),
      purchaseQueryKey: getQueryKey('hasPurchased', productCode, accountId, {
        siteId,
      }),
    },
    [productTypes.BH_BACKUP_FAMILY]: {
      data: {
        productCode: productTypes.BH_BACKUP_FAMILY,
        parentProdInstId,
        purchaseTerm: '1',
        purchaseTermUnit: 'YEAR',
        adTrackingBannerId: adTrackingBannerId,
      },
      name: t('productNames.bhBackup'),
      purchaseQueryKey: getQueryKey('hasPurchased', productCode, accountId, {
        siteId,
      }),
    },
    [productTypes.XCITIUM_UPGRADE]: {
      data: {
        action: 'upgrade',
        adTrackingBannerId: xcitiumAdTrackingBannerId,
      },
      name: t('productNames.xcitium'),
      purchaseQueryKey: getQueryKey('hasPurchased', productCode, accountId),
    },
    [productTypes.JETPACK_BACKUP_FAMILY]: {
      data: {
        productCode: productTypes.JETPACK_BACKUP_FAMILY,
        parentProdInstId,
        adTrackingBannerId: 'P13C100S1N0B3003A1D115E0000V111',
        // parentProduct: siteId, // also required, but should be passed in from current UI with data prop
      },
      name: t('productNames.jetpack'),
      purchaseQueryKey: getQueryKey('hasPurchased', productCode, accountId, {
        siteId,
      }),
    },
    [productTypes.JETPACK_BACKUP_T1]: {
      data: {
        action: 'upgrade',
        //prodInstId: //Required - HAL addon backreference Id - passed in from current UI with data prop
        adTrackingBannerId: 'PP13C100S1N0B3003A151D115E0000V106',
      },
      name: t('productNames.jetpack'),
      purchaseQueryKey: getQueryKey('hasPurchased', productCode, accountId, {
        siteId,
      }),
    },
    // WP_SOLUTION_FAMILY CTB
    [productTypes.WP_SOLUTION_FAMILY]: {
      data: {
        productCode: productTypes.WP_SOLUTION_FAMILY,
        parentProdInstId,
        adTrackingBannerId: 'P195C100S1N0B3003A151D115E0000V100',
        parentProduct: {
          siteId,
          siteUrl,
        },
      },
      name: t('productNames.wpSolution'),
      purchaseQueryKey: getQueryKey('hasPurchased', productCode, accountId, {
        siteId,
      }),
    },
    // WP_SOLUTION_UPGRADE CTB
    [productTypes.WP_SOLUTION_UPGRADE]: {
      data: {
        action: 'upgrade',
        adTrackingBannerId: 'P195C100S1N0B3003A151D115E0000V100',
      },
      name: t('productNames.wpSolution'),
      purchaseQueryKey: getQueryKey('hasPurchased', productCode, accountId, {
        siteId,
      }),
    },
    [productTypes.DOM_FAMILY]: {
      data: {
        productCode: productTypes.DOM_FAMILY,
        purchaseTerm: '1',
        purchaseTermUnit: 'YEAR',
        adTrackingBannerId: 'P13C100S1N0B3003A151D115E0000V147',
      },
      name: t('productNames.domainPurchase'),
      purchaseQueryKey: getQueryKey('hasPurchased', productCode, accountId),
    },
    [productTypes.BH_PREM_BKUP_ADDL_STORAGE]: {
      data: {
        productCode: productTypes.BH_PREM_BKUP_ADDL_STORAGE,
        purchaseTermUnit: 'YEAR',
        purchaseTerm: '1',
        prodInstId: parentProdInstId,
        used_bytes: usedBytes,
        allowed_limit_bytes: allowedLimitBytes,
        adTrackingBannerId: adTrackingBannerId,
      },
      name: t('productNames.premiumBackupAdditionalStorage'),
      purchaseQueryKey: getQueryKey('hasPurchased', productCode, accountId),
    },
    [productTypes.CPANEL_FAMILY]: {
      data: {
        productCode: productTypes.CPANEL_FAMILY,
        parentProdInstId,
        adTrackingBannerId: 'P13C100S1N0B3003A1D115E0000V111',
      },
      name: t('productNames.cpanel'),
      purchaseQueryKey: getQueryKey('hasPurchased', productCode, accountId),
    },
    [productTypes.CPANEL_UPGRADE]: {
      data: {
        action: 'upgrade',
        adTrackingBannerId: 'P13C100S1N0B3003A1D115E0000V111',
      },
      name: t('productNames.cpanel'),
      purchaseQueryKey: getQueryKey('hasPurchased', productCode, accountId),
    },
    [productTypes.BH_BACKUP_UPGRADE]: {
      data: {
        action: 'upgrade',
        prodInstId: parentProdInstId,
        adTrackingBannerId: adTrackingBannerId,
      },
      name: t('productNames.backupUpgrade'),
      purchaseQueryKey: getQueryKey('hasPurchased', productCode, accountId),
    },
    [productTypes.PRO_DESIGN_LIVE_FAMILY]: {
      data: {
        productCode: productTypes.PRO_DESIGN_LIVE_FAMILY,
        parentProdInstId,
        adTrackingBannerId: 'P13C100S1N0B3003A151D115E0000V153',
      },
      name: t('productNames.proDesignLive'),
      purchaseQueryKey: getQueryKey('hasPurchased', productCode, accountId, {
        siteId,
      }),
    },
    [productTypes.MONARX_FAMILY]: {
      data: {
        productCode: productTypes.MONARX_FAMILY,
        parentProdInstId,
        adTrackingBannerId: 'P99C46097236S1N0B1A151D0E0000V100',
      },
      name: t('productNames.monarxFamily'),
      purchaseQueryKey: getQueryKey('hasPurchased', productCode, accountId),
    },
    [productTypes.AI_CREDIT]: {
      data: {
        brand: brandFromJWT,
        productCode: productTypes.AI_CREDIT,
        adTrackingBannerId: 'P13C100S1N0B3003A1D115E0000V108',
      },
      name: t('productNames.aiCredits'),
      purchaseQueryKey: getQueryKey('hasPurchased', productCode, accountId),
    },

    // FUTURE PRODUCTS HERE...
    // [PRODUCT_TYPES.YOAST_SEO]: {},
  };

  return ctbProducts[productCode];
};

type QueryKeyType = (
  | string
  | {
      siteId?: string;
      productCode: ProductCodeTypes;
    }
)[];
export const useCtbPurchaseStatusQuery = (queryKey: QueryKeyType) => {
  const result = useQuery({
    queryKey,
    queryFn: () => {
      return { hasPurchasedRecently: false };
    },
    enabled: true,
    staleTime: 1000 * 60 * 4, // fresh data can be fetched after 4 minutes
    refetchInterval: 1000 * 60 * 4,
  });

  const hasPurchasedRecently =
    result.isPending || result.isRefetching
      ? false
      : result?.data?.hasPurchasedRecently;

  return { ...result, hasPurchasedRecently };
};

/* When the product info query from HUAPI eventually becomes available,
  we'll call it in the queryFn above and remove setCtbPurchaseQueryData entirely */
export const setCtbPurchaseQueryData = (
  queryClient: QueryClient,
  queryKey: QueryKeyType,
  data: {
    hasPurchasedRecently: boolean;
  },
) => {
  queryClient.setQueryData(queryKey, data);
};
